import { useState, useEffect } from 'react';
import { createFileRoute, useParams, Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Copy, Check, ArrowLeft, AlertCircle } from 'lucide-react';
import { fetchApi } from '@/lib/api';

// Reuse the ApiEvent interface if needed, or define a specific one
interface ApiEvent {
  id: number;
  name: string;
  event_link_id: string;
  // Add other fields if needed for display
}

// NOTE: Route path updated
export const Route = createFileRoute('/_authenticated/events/$eventId/share')({
  component: ShareEventPage,
  // Optional: Add a loader to fetch data before rendering
});

function ShareEventPage() {
  const params = useParams({ from: '/_authenticated/events/$eventId/share' });
  const eventId = params.eventId; // No need for select if the param name matches

  const [event, setEvent] = useState<ApiEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Validate parsedEventId - ensure it's a number before using in API calls
  const parsedEventId = parseInt(eventId, 10);
  const isValidEventId = !isNaN(parsedEventId);

  useEffect(() => {
    if (!isValidEventId) {
        setError('Invalid Event ID provided in URL.');
        setLoading(false);
        return;
    }

    const fetchEventDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        // API uses /api/admin/events/{id}
        const response = await fetchApi(`/api/admin/events/${parsedEventId}`);
        if (response.status === 404) {
          throw new Error('Event not found or you do not have access.');
        }
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data: ApiEvent = await response.json();
        setEvent(data);
      } catch (err) {
        console.error('Failed to fetch event details:', err);
        setError(
          err instanceof Error ? err.message : 'An unknown error occurred',
        );
      } finally {
        setLoading(false);
      }
    };

    fetchEventDetails();
  }, [parsedEventId, isValidEventId]);

  const eventPublicLink = event
    ? `${window.location.origin}/event/${event.event_link_id}`
    : '';
  const qrCodeUrl = event ? `/api/qrcodes/${event.event_link_id}.png` : '';

  const handleCopyLink = () => {
    if (!eventPublicLink) return;
    navigator.clipboard.writeText(eventPublicLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // Reset copied state after 2 seconds
    });
  };

  if (loading) {
    return (
      <div className="space-y-4">
         <div className="flex items-center justify-between">
             <Skeleton className="h-8 w-32" /> {/* Back button placeholder */}
             <Skeleton className="h-8 w-48" /> {/* Title placeholder */}
             <div className="w-24"></div> {/* Spacer */}
         </div>
      <Card>
        <CardHeader>
             <Skeleton className="h-6 w-3/4 mb-2" />
          <Skeleton className="h-4 w-1/2" />
        </CardHeader>
          <CardContent className="grid md:grid-cols-2 gap-6">
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-40 w-full" />
        </CardContent>
      </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
         {/* Basic header structure even on error */}
          <div className="flex items-center justify-between">
            <Button variant="outline" size="sm" asChild>
               <Link to="/events">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Events
               </Link>
            </Button>
            <h1 className="text-2xl font-semibold text-destructive">Error</h1>
            <div className="w-24"></div>
        </div>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Failed to Load Event Details</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!event) {
      // This case should ideally be covered by loading/error states,
      // but acts as a fallback if fetch succeeds but returns unexpected null/empty data.
      return (
        <div className="space-y-4">
           <div className="flex items-center justify-between">
                <Button variant="outline" size="sm" asChild>
                   <Link to="/events">
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Back to Events
                   </Link>
                </Button>
                <h1 className="text-2xl font-semibold">Event Not Found</h1>
                <div className="w-24"></div>
            </div>
             <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Event Data Missing</AlertTitle>
                <AlertDescription>Could not retrieve details for this event.</AlertDescription>
             </Alert>
        </div>
      )
  }

  return (
    <div className="space-y-4">
       <div className="flex items-center justify-between">
         <Button variant="outline" size="sm" asChild>
            <Link to="/events">
               <ArrowLeft className="mr-2 h-4 w-4" />
               Back to Events
            </Link>
         </Button>
          <h1 className="text-2xl font-semibold truncate" title={`Share Event: ${event.name}`}>
            Share: {event.name}
          </h1>
         <div className="w-24"></div> {/* Spacer */}
       </div>

    <Card>
      <CardHeader>
          <CardTitle>Share Your Event</CardTitle>
        <CardDescription>
            Guests can access the event using the link or QR code below.
        </CardDescription>
      </CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-6 items-start">
          {/* Link Section */}
        <div className="space-y-2">
            <Label htmlFor="event-link">Event Link</Label>
          <div className="flex space-x-2">
              <Input id="event-link" value={eventPublicLink} readOnly />
              <Button variant="outline" size="icon" onClick={handleCopyLink} aria-label="Copy link">
                {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Share this link directly with your guests.
            </p>
             {/* Add a button to open the public link in a new tab */}
            <Button variant="secondary" size="sm" asChild>
               <a href={eventPublicLink} target="_blank" rel="noopener noreferrer">
                  View Public Page
               </a>
            </Button>
        </div>

          {/* QR Code Section */}
          <div className="space-y-2 flex flex-col items-center">
          <Label>QR Code</Label>
             {qrCodeUrl ? (
               <div className="bg-white p-4 rounded-md border inline-block">
                 <img src={qrCodeUrl} alt={`QR Code for ${event.name}`} width={160} height={160} />
               </div>
             ) : (
                <Skeleton className="h-40 w-40" />
             )}
             <p className="text-xs text-muted-foreground text-center">
               Display this QR code for guests to scan.
             </p>
          {qrCodeUrl && (
                <Button variant="secondary" size="sm" asChild>
                   <a href={qrCodeUrl} download={`${event.event_link_id}_qrcode.png`}>
                      Download QR Code
                   </a>
                </Button>
          )}
        </div>
      </CardContent>
    </Card>
    </div>
  );
}
